import { AlertTriangle, RefreshCw } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

// Error types from backend (must match ByocErrorReason in byoc.dto.ts)
export type ByocErrorReason =
  | 'oauth_refresh_failed'
  | 'api_key_invalid'
  | 'spending_cap_reached'
  | 'network_error'
  | 'decryption_failed';

interface ByocExpiredModalProps {
  open: boolean;
  errorType: ByocErrorReason | null;
  title?: string;
  message?: string;
  /**
   * Whether this user can actually reach the BYOC card to reconnect. Reconnect navigates to
   * Settings → Workspace, and that tab is admin/owner-only (`Settings.tsx` gates it on
   * `isAdminOrOwner`), so offering the button to anyone else is a dead end. Defaults to true so
   * existing callers keep their behaviour.
   */
  canReconnect?: boolean;
  onReconnect: () => void;
  onContinue: () => void;
}

// Fallback for when the backend omits title/message. Keep in sync with BYOC_ERROR_MESSAGES in
// apps/api/src/workspaces/dto/byoc.dto.ts.
const DEFAULT_MESSAGES: Record<ByocErrorReason, { title: string; message: string }> = {
  oauth_refresh_failed: {
    title: 'Claude Session Expired',
    message: 'Your Claude session expired, so this workspace has switched to workspace credits. Send your message again to continue, or reconnect Claude.',
  },
  api_key_invalid: {
    title: 'API Key Invalid',
    message: 'Your API key is invalid or revoked, so this workspace has switched to workspace credits. Send your message again to continue, or reconnect Claude.',
  },
  spending_cap_reached: {
    title: 'Spending Cap Reached',
    message: 'Your Claude spending cap has been reached, so this workspace has switched to workspace credits. Send your message again to continue.',
  },
  network_error: {
    title: 'Connection Issue',
    message: 'We couldn\'t reach Claude to verify your connection. Your Claude account is still connected — please send your message again.',
  },
  decryption_failed: {
    title: 'Connection Error',
    message: 'We couldn\'t read your stored credentials, so this workspace has switched to workspace credits. Send your message again to continue, or reconnect Claude.',
  },
};

export function ByocExpiredModal({
  open,
  errorType,
  title,
  message,
  canReconnect = true,
  onReconnect,
  onContinue,
}: ByocExpiredModalProps) {
  // Use provided title/message or fall back to defaults
  const defaultContent = errorType ? DEFAULT_MESSAGES[errorType] : DEFAULT_MESSAGES.oauth_refresh_failed;
  const displayTitle = title || defaultContent.title;
  const displayMessage = message || defaultContent.message;

  // Hide reconnect for spending cap - reconnecting won't help until cap resets
  const isSpendingCap = errorType === 'spending_cap_reached';
  const showReconnect = !isSpendingCap && canReconnect;
  // Reconnect would help, but this user cannot get to the BYOC card — point them at someone who can
  // instead of offering a button that leads to a tab they cannot open.
  const needsAdminToReconnect = !isSpendingCap && !canReconnect;

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-[360px] p-5"
        hideCloseButton
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="space-y-2">
          <div className="flex items-center gap-2.5">
            <div className="rounded-full bg-amber-100 dark:bg-amber-900/30 p-1.5">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            </div>
            <DialogTitle className="text-base">{displayTitle}</DialogTitle>
          </div>
          <DialogDescription className="text-sm space-y-2">
            <p>{displayMessage}</p>
            {showReconnect && <p>To continue using your Claude account, reconnect below.</p>}
            {needsAdminToReconnect && (
              <p>Ask your workspace owner or admin to reconnect Claude.</p>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 mt-3">
          <Button
            variant={showReconnect ? 'outline' : 'default'}
            size="sm"
            onClick={onContinue}
            className="flex-1"
          >
            {needsAdminToReconnect ? 'Got it' : 'Continue'}
          </Button>
          {showReconnect && (
            <Button
              size="sm"
              onClick={onReconnect}
              className="flex-1"
            >
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              Reconnect
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
